import { useId, useState } from 'react'
import { Download, FileSpreadsheet, Loader2, X } from 'lucide-react'
import { downloadReport, useReportTypes } from '@/api/reports'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { headerButton } from './headerButton'

/**
 * "LPC Reports" in the header: pick a report type and download it as Excel. It opens under the
 * header link as the original site's dialog: a grey title bar with a close button, the report
 * type, and Download Report on the right.
 */
export function ReportsMenu() {
  const [open, setOpen] = useState(false)
  const [reportKey, setReportKey] = useState<string>()
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string>()
  const { data: types, isPending, isError } = useReportTypes(open)
  const selectId = useId()

  // The first report type until one is picked
  const selectedKey = reportKey ?? types?.[0]?.key

  const download = async () => {
    if (!selectedKey) return
    setDownloading(true)
    setError(undefined)
    try {
      await downloadReport(selectedKey)
    } catch {
      setError("The report couldn't be downloaded. Please try again.")
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={headerButton}
          aria-label="LPC Reports"
        >
          <FileSpreadsheet aria-hidden="true" />
          <span className="hidden sm:inline">LPC </span>Reports
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        // Keep a 16px margin from the window's edges on phones
        collisionPadding={16}
        className="w-[min(380px,calc(100vw-2rem))] gap-0 overflow-hidden p-0"
        aria-labelledby={`${selectId}-title`}
      >
        <div className="flex items-center justify-between gap-2 border-b bg-muted py-2 pr-2 pl-4">
          <h2 id={`${selectId}-title`} className="text-lg font-semibold">
            LPC Reports
          </h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <X />
          </Button>
        </div>
        <div className="flex flex-col gap-2 p-4">
          {isError ? (
            <p role="alert" className="text-sm text-destructive">
              The report types couldn&apos;t be loaded.
            </p>
          ) : (
            <>
              <label htmlFor={selectId} className="text-sm font-medium">
                Select Report Type
              </label>
              <Select
                value={selectedKey}
                onValueChange={setReportKey}
                disabled={isPending}
              >
                <SelectTrigger id={selectId} className="w-full">
                  <SelectValue
                    placeholder={isPending ? 'Loading…' : 'Choose a report'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {types?.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
        <div className="flex justify-end px-4 pb-4">
          <Button
            onClick={download}
            disabled={!selectedKey || downloading}
            aria-label="Download selected report"
          >
            {downloading ? <Loader2 className="animate-spin" /> : <Download />}
            Download Report
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
